'use client';

import { useEffect, useState, useContext } from 'react';
import {
    CheckCircleIcon,
    FolderIcon,
    XMarkIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    DocumentArrowDownIcon,
} from '@heroicons/react/20/solid';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import MeetingAlert from './meetingalert';
import MessageAlert from './messagealert';
import ShiftsAlert from './shiftsstartalert';
import AIAssistant from './aiassistant';
import { AuthContext } from '../AuthContext';
import ManagerNav from './managernav';
import SickLeaveGraph from './sickleavegraph';
import StaffDetails from './individalstaff';

export default function Admin() {
    const [AI, SetAI] = useState(false);
    const [unfinishedForms, setUnfinishedForms] = useState([]);
    const [unfinishedFormsCount, setUnfinishedFormsCount] = useState(0);
    const [allForms, setAllForms] = useState([]);
    const [allFormsCount, setAllFormsCount] = useState(0);
    const [filteredForms, setFilteredForms] = useState([]);
    const [selectedForm, setSelectedForm] = useState(null);
    const [meeting, setMeeting] = useState(false);
    const [comment, setComment] = useState('');
    const [fit, setFit] = useState(1);
    const [support, setSupport] = useState(1);
    const [sickChatEntries, setSickChatEntries] = useState([]);
    const [sickChatLoading, setSickChatLoading] = useState(false);
    const [sickChatError, setSickChatError] = useState('');
    const [staff, setStaff] = useState([]);
    const [tableHovered, setTableHovered] = useState(false);
    const [report, setReport] = useState([]);
    const [selectedReportIndex, setSelectedReportIndex] = useState(0);
    const [showReport, setShowReport] = useState(false);
    const [showGraph, setShowGraph] = useState(true);
    const [pdfSendMessage, setPdfSendMessage] = useState('');
    const [pdfSendType, setPdfSendType] = useState('success');
    const [pdfSendVisible, setPdfSendVisible] = useState(false);
    
    // Filter states
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterStaffName, setFilterStaffName] = useState('all');
    const [quickFilter, setQuickFilter] = useState('');
    const [isFiltered, setIsFiltered] = useState(false);
    const [trendAnalysisLoading, setTrendAnalysisLoading] = useState(false);
    const [trendStatusMessage, setTrendStatusMessage] = useState('');

    const navigate = useNavigate();
    const user = useContext(AuthContext);

    const fetchBackToWorkForms = async () => {
        try {
            const response = await fetch('https://api.insightos.co.uk/api/backtoworkdata', {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });

            if (!response.ok) throw new Error('Network response was not ok');
            const data = await response.json();

            const filteredData = data.data.filter(item => item.MeetingHeld === 0);
            setUnfinishedForms(filteredData);
            setUnfinishedFormsCount(filteredData.length);
            setAllForms(data.data);
            setAllFormsCount(data.data.length);

            // Format report content using regex
            const formattedReports = Array.isArray(data.reports)
                ? data.reports.map(r => ({
                        ...r,
                        Content: r.Content
                            ? r.Content
                                    .replace(/\[SECTION: (.+?)\]/g, '\n\n$1\n' + '-'.repeat(40))
                                    .replace(/(?<!\n)\n(?!\n)/g, ' ')
                                    .replace(/\n{2,}/g, '\n\n')
                            : '',
                    }))
                : [];
            setReport(formattedReports);

            if (Array.isArray(data.staff)) {
                const sortedStaff = data.staff
                    .slice()
                    .sort((a, b) => {
                        const nameA = a.StaffName || '';
                        const nameB = b.StaffName || '';
                        const nameCompare = nameA.localeCompare(nameB);
                        if (nameCompare !== 0) return nameCompare;
                        const dateA = a.ReturnDate ? new Date(a.ReturnDate) : new Date(0);
                        const dateB = b.ReturnDate ? new Date(b.ReturnDate) : new Date(0);
                        return dateB - dateA;
                    });
                setStaff(sortedStaff);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    useEffect(() => {
        fetchBackToWorkForms();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [allForms, filterStartDate, filterEndDate, filterStaffName, quickFilter]);

    useEffect(() => {
        const loadSickChatEntries = async () => {
            const selectedFormId = getBackToWorkFormId(selectedForm);
            if (!selectedFormId) {
                setSickChatEntries([]);
                setSickChatError('');
                setSickChatLoading(false);
                return;
            }

            setSickChatLoading(true);
            setSickChatError('');
            try {
                const response = await fetch(`https://api.insightos.co.uk/api/sickchat?id=${encodeURIComponent(selectedFormId)}`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                });

                const data = await response.json().catch(() => ({}));
                if (!response.ok || !data.success) {
                    throw new Error(data.message || 'Failed to load sick chat entries');
                }

                setSickChatEntries(Array.isArray(data.data) ? data.data : []);
            } catch (error) {
                console.error('Error loading sick chat entries:', error);
                setSickChatEntries([]);
                setSickChatError(error?.message || 'Failed to load sick chat entries');
            } finally {
                setSickChatLoading(false);
            }
        };

        loadSickChatEntries();
    }, [selectedForm]);

    const getQuickFilterRange = (filterKey) => {
        if (!filterKey) return null;

        const now = new Date();
        const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        let startDate = null;
        let endDate = endOfToday;

        switch (filterKey) {
            case 'lastMonth': {
                const startOfPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                const endOfPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
                startDate = startOfPrevMonth;
                endDate = endOfPrevMonth;
                break;
            }
            case 'last6Months': {
                const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                start.setMonth(start.getMonth() - 6);
                startDate = start;
                break;
            }
            case 'lastYear': {
                const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                start.setFullYear(start.getFullYear() - 1);
                startDate = start;
                break;
            }
            default:
                return null;
        }

        if (!startDate) return null;
        return { start: startDate, end: endDate };
    };

    const applyFilters = () => {
        let filtered = [...allForms];
        let hasActiveFilter = false;

        const quickRange = getQuickFilterRange(quickFilter);

        // Apply quick filter
        if (quickRange) {
            hasActiveFilter = true;
            filtered = filtered.filter(form => {
                const formDate = new Date(form.StartDate);
                return formDate >= quickRange.start && formDate <= quickRange.end;
            });
        }

        // Apply date range filters
        if (filterStartDate) {
            hasActiveFilter = true;
            filtered = filtered.filter(form => {
                const formDate = new Date(form.StartDate);
                const filterDate = new Date(filterStartDate);
                return formDate >= filterDate;
            });
        }

        if (filterEndDate) {
            hasActiveFilter = true;
            filtered = filtered.filter(form => {
                const formDate = new Date(form.StartDate);
                const filterDate = new Date(filterEndDate);
                return formDate <= filterDate;
            });
        }

        // Apply staff name filter
        if (filterStaffName && filterStaffName !== 'all') {
            hasActiveFilter = true;
            filtered = filtered.filter(form =>
                form.StaffName === filterStaffName
            );
        }

        setFilteredForms(filtered);
        setIsFiltered(hasActiveFilter);
    };

    // Get unique staff names for dropdown
    const uniqueStaffNames = [...new Set(allForms.map(form => form.StaffName).filter(Boolean))].sort();

    const selectedStaffWithTrends = filterStaffName !== 'all'
        ? staff.find((member) => member.StaffName === filterStaffName && (member.SickTrends || member.StaffTrends))
            || staff.find((member) => member.StaffName === filterStaffName)
        : null;

    const sickTrendsContent = selectedStaffWithTrends?.SickTrends ?? selectedStaffWithTrends?.StaffTrends ?? '';
    const sickTrendsTimestamp = selectedStaffWithTrends?.SickTrendsCreated ?? selectedStaffWithTrends?.StaffTrendsCreated ?? null;

    const clearFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterStaffName('all');
        setQuickFilter('');
        setIsFiltered(false);
        setTrendStatusMessage('');
    };

    const handleTrendAnalysis = async () => {
        if (filteredForms.length === 0) {
            setTrendStatusMessage('No data to analyze. Please refine your filters first.');
            return;
        }

        setTrendStatusMessage('');
        setTrendAnalysisLoading(true);

        try {
            // Group by staff
            const staffGroups = {};
            filteredForms.forEach(form => {
                if (!staffGroups[form.StaffId]) {
                    staffGroups[form.StaffId] = [];
                }
                staffGroups[form.StaffId].push(form);
            });

            // Determine requested date range
            const manualStart = filterStartDate ? new Date(filterStartDate) : null;
            const manualEnd = filterEndDate ? new Date(filterEndDate) : null;
            const quickRange = getQuickFilterRange(quickFilter);

            const formDates = filteredForms.map(f => new Date(f.StartDate));
            const fallbackStart = new Date(Math.min(...formDates));
            const fallbackEnd = new Date(Math.max(...formDates));

            const startDate = manualStart || quickRange?.start || fallbackStart;
            const endDate = manualEnd || quickRange?.end || fallbackEnd;

            const toISODate = (date) => date.toISOString().split('T')[0];

            const staffUpdates = new Map();

            // Send for each staff member
            for (const [staffIdKey] of Object.entries(staffGroups)) {
                const numericStaffId = Number(staffIdKey);
                if (Number.isNaN(numericStaffId)) continue;

                const response = await fetch('/api/staffsickreport', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        staffId: numericStaffId,
                        startDate: toISODate(startDate),
                        endDate: toISODate(endDate),
                    })
                });

                const result = await response.json();

                if (!response.ok || !result.success) {
                    throw new Error(result.message || 'Failed to complete trend analysis.');
                }

                staffUpdates.set(numericStaffId, {
                    trends: result.trends || 'Analysis generated successfully.',
                    generatedAt: result.generatedAt || new Date().toISOString(),
                });
            }

            if (staffUpdates.size > 0) {
                setStaff((prev) =>
                    prev.map((member) => {
                        if (!member || member.StaffId === undefined) return member;
                        const update = staffUpdates.get(Number(member.StaffId));
                        if (!update) return member;
                        return {
                            ...member,
                            SickTrends: update.trends,
                            StaffTrends: update.trends,
                            SickTrendsCreated: update.generatedAt,
                            StaffTrendsCreated: update.generatedAt,
                        };
                    })
                );

                setTrendStatusMessage(
                    `Trend analysis refreshed for ${staffUpdates.size} staff member${staffUpdates.size > 1 ? 's' : ''}.`
                );
            } else {
                setTrendStatusMessage('No staff members matched the current filters.');
            }
        } catch (error) {
            console.error('Trend analysis error:', error);
            setTrendStatusMessage(error.message || 'Failed to complete trend analysis. Please try again.');
        } finally {
            setTrendAnalysisLoading(false);
        }
    };

    const stats = [
        { name: 'Outstanding Forms', stat: `${unfinishedFormsCount}` },
        { name: 'Total Forms', stat: `${allFormsCount}` },
    ];

    if (AI) {
        return <AIAssistant />;
    }

    const formattedDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            return format(parseISO(dateStr), 'dd/MM/yyyy');
        } catch {
            return dateStr;
        }
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '';
        try {
            return format(parseISO(dateStr), 'dd/MM/yyyy HH:mm');
        } catch {
            return dateStr;
        }
    };

    const handleSaveMeeting = async (id) => {
        if (id === null || id === undefined || id === '') {
            console.error('Cannot save meeting without a back-to-work form id');
            return;
        }

        try {
            const response = await fetch('https://api.insightos.co.uk/api/backtoworkmeeting', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    id,
                    comment,
                    fit: fit,
                    support: support,
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.message || `Request failed with status ${response.status}`);
            }

            if (!data.success) throw new Error('Failed to save meeting data');

            setSelectedForm(null);
            setComment('');
            setFit(1);
            setMeeting(false);
            fetchBackToWorkForms();
        } catch (error) {
            console.error('Error saving meeting:', error);
        }
    };

    const handleSickLeaveReport = async () => {
        try {
            const response = await fetch('/api/weekly-sick-report', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });

            if (!response.ok) throw new Error('Network response was not ok');
            const data = await response.json();

            if (data.success) {
                alert('Monthly sick leave report generated successfully!');
                fetchBackToWorkForms();
            } else {
                alert('Failed to generate report: ' + (data.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error generating report:', error);
            alert('Error generating report. Please try again.');
        }
    };

    const getBackToWorkFormId = (record) => {
        if (!record || typeof record !== 'object') return null;
        return record.Id ?? record.id ?? null;
    };

    const handleDownloadFormPdf = async (formId) => {
        if (formId === null || formId === undefined || formId === '') {
            console.error('Failed to send back to work PDF: missing form id', formId);
            setPdfSendMessage('Unable to send PDF: form ID is missing. Please refresh and try again.');
            setPdfSendType('error');
            setPdfSendVisible(true);
            setTimeout(() => setPdfSendVisible(false), 7000);
            return;
        }

        try {
            const response = await fetch(`https://api.insightos.co.uk/api/backtowork/${formId}/pdf`, {
                method: 'GET',
                credentials: 'include',
            });

            if (!response.ok) {
                const text = await response.text().catch(() => '');
                console.error('Failed to send back to work PDF, non-ok response', text);
                setPdfSendMessage('Failed to send PDF. Please try again.');
                setPdfSendType('error');
                setPdfSendVisible(true);
                setTimeout(() => setPdfSendVisible(false), 7000);
                return;
            }

            const data = await response.json();
            if (data.success) {
                setPdfSendMessage(data.message || 'PDF sent — check your email.');
                setPdfSendType('success');
                setPdfSendVisible(true);
                setTimeout(() => setPdfSendVisible(false), 5000);
            } else {
                console.error('Send PDF response error:', data);
                setPdfSendMessage(data.message || 'Failed to send PDF. Please try again.');
                setPdfSendType('error');
                setPdfSendVisible(true);
                setTimeout(() => setPdfSendVisible(false), 7000);
            }
        } catch (error) {
            console.error('Failed to send back to work PDF', error);
            setPdfSendMessage(error?.message || 'Failed to send PDF. Please try again.');
            setPdfSendType('error');
            setPdfSendVisible(true);
            setTimeout(() => setPdfSendVisible(false), 7000);
        }
    };

    // Helper for report tab label
    const getReportTabLabel = (reportItem) => {
        if (!reportItem?.FromDate) return 'Unknown';
        try {
            return format(parseISO(reportItem.FromDate), 'MMM yyyy');
        } catch {
            return reportItem.FromDate;
        }
    };

    return (
        <>
            <ManagerNav />
            <div className="min-h-screen flex flex-col bg-gray-50">
                {/* Stats */}
                <div className="w-full max-w-7xl mx-auto px-4 py-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                        {stats.map((item) => (
                            <div
                                key={item.name}
                                className="rounded-xl bg-white px-6 py-5 shadow-md flex flex-col items-center border border-gray-100"
                            >
                                <dt className="truncate text-sm font-medium text-gray-500">{item.name}</dt>
                                <dd className="mt-2 text-3xl font-bold tracking-tight text-emerald-600">{item.stat}</dd>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Graph Section */}
                <div className="w-full max-w-3xl mx-auto mb-6">
                    <div className="flex flex-col items-center">
                        <button
                            className="flex items-center gap-2 mb-2 px-4 py-2 rounded bg-indigo-500 text-white hover:bg-emerald-600 transition"
                            onClick={() => setShowGraph((prev) => !prev)}
                            aria-expanded={showGraph}
                        >
                            <span>Sick Leave Graph</span>
                            {showGraph ? (
                                <ChevronUpIcon className="w-5 h-5" />
                            ) : (
                                <ChevronDownIcon className="w-5 h-5" />
                            )}
                        </button>
                        {showGraph && (
                            <div className="w-full bg-white rounded-xl shadow-md p-4 flex justify-center">
                                <SickLeaveGraph />
                            </div>
                        )}
                    </div>
                </div>

                {/* Report Section */}
                <div className="w-full max-w-5xl mx-auto mb-8">
                    <div className="flex flex-col items-center">
                        <button
                            className="flex items-center gap-2 mb-2 px-4 py-2 rounded bg-emerald-500 text-white hover:bg-emerald-600 transition"
                            onClick={() => setShowReport((prev) => !prev)}
                            aria-expanded={showReport}
                        >
                            <span>Monthly Sick Leave Reports</span>
                            {showReport ? (
                                <ChevronUpIcon className="w-5 h-5" />
                            ) : (
                                <ChevronDownIcon className="w-5 h-5" />
                            )}
                        </button>
                        {showReport && (
                            <div className="w-full bg-white rounded-xl shadow-md p-6">
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {report.map((item, idx) => (
                                        <button
                                            key={item.id}
                                            className={`px-4 py-2 rounded-t transition ${
                                                selectedReportIndex === idx
                                                    ? 'bg-emerald-500 text-white'
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                            onClick={() => setSelectedReportIndex(idx)}
                                        >
                                            {getReportTabLabel(item)}
                                        </button>
                                    ))}
                                </div>
                                <div className="w-full text-sm bg-white rounded-b px-4 py-5 min-h-[60px] whitespace-pre-line border border-gray-100">
                                    {report[selectedReportIndex]?.Content || (
                                        <span className="text-gray-400">No report selected.</span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Unfinished Forms */}
                {!selectedForm && (
                    <div className="w-full max-w-5xl mx-auto mb-8">
                        <div className="bg-white rounded-xl shadow-md p-6">
                            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                                Unfinished Back to Work Forms
                            </h3>
                            <p className="mb-5 text-sm text-gray-500 text-center">
                                These are the forms that are not completed yet.
                            </p>
                            <ul role="list" className="divide-y divide-gray-100">
                                {unfinishedForms.map((person) => {
                                    const getInitials = (name) => {
                                        if (!name) return '';
                                        const parts = name.trim().split(' ');
                                        if (parts.length === 1) return parts[0][0]?.toUpperCase() || '';
                                        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
                                    };
                                    const initials = getInitials(person.StaffName);

                                    const formatDate = (dateStr) => {
                                        if (!dateStr) return '';
                                        try {
                                            return format(parseISO(dateStr), 'dd/MM/yyyy');
                                        } catch {
                                            return dateStr;
                                        }
                                    };

                                    return (
                                        <li
                                            key={person.Id}
                                            className="flex justify-between gap-x-6 py-5 bg-red-50 hover:bg-red-100 cursor-pointer rounded transition"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedForm(person);
                                            }}
                                        >
                                            <div className="flex min-w-0 gap-x-4">
                                                <div className="size-12 flex-none rounded-full bg-red-500 flex items-center justify-center text-white text-lg font-bold">
                                                    {initials}
                                                </div>
                                                <div className="min-w-0 flex-auto">
                                                    <p className="text-sm font-semibold text-gray-900">{person.StaffName}</p>
                                                    <p className="mt-1 truncate text-xs text-gray-500">
                                                        {formatDate(person.StartDate)} - {formatDate(person.ReturnDate)}
                                                    </p>
                                                    <button
                                                        className="text-xs text-blue-500 hover:text-blue-700"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedForm(person);
                                                        }}
                                                    >
                                                        View Form
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="hidden shrink-0 sm:flex sm:flex-col sm:items-end">
                                                <p className="text-sm text-gray-900">Manager: {person.ManagerName}</p>
                                                {person.MeetingHeld === 0 ? (
                                                    <div className="mt-1 flex items-center gap-x-1.5">
                                                        <div className="flex-none rounded-full bg-emerald-500/20 p-1">
                                                            <div className="size-1.5 rounded-full bg-red-500" />
                                                        </div>
                                                        <p className="text-xs text-gray-500">Not Completed</p>
                                                    </div>
                                                ) : (
                                                    <div className="mt-1 flex items-center gap-x-1.5">
                                                        <div className="flex-none rounded-full bg-emerald-500/20 p-1">
                                                            <div className="size-1.5 rounded-full bg-emerald-500" />
                                                        </div>
                                                        <p className="text-xs text-gray-500">Completed</p>
                                                    </div>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                )}

                {/* Selected Form Fullscreen Meeting Workspace */}
                {selectedForm && (
                    <div className="fixed inset-0 z-[80] bg-slate-950/70 backdrop-blur-sm">
                        <div className="h-full w-full bg-white">
                            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">Back To Work Meeting Workspace</h3>
                                    <p className="text-sm text-gray-500">{selectedForm.StaffName} • {formattedDate(selectedForm.StartDate)} to {formattedDate(selectedForm.ReturnDate)}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleDownloadFormPdf(getBackToWorkFormId(selectedForm))}
                                        className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                                    >
                                        <DocumentArrowDownIcon className="h-5 w-5" />
                                        Send PDF (Email)
                                    </button>
                                    {!meeting ? (
                                        <button
                                            onClick={() => setMeeting(true)}
                                            type="button"
                                            className="inline-flex items-center gap-x-2 rounded-md bg-emerald-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-600 transition"
                                        >
                                            <CheckCircleIcon aria-hidden="true" className="size-5" />
                                            Start Meeting
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleSaveMeeting(getBackToWorkFormId(selectedForm))}
                                            type="button"
                                            data-component="backtowork-dashboard-actions"
                                            data-feature="save_meeting_post"
                                            data-time-saved="10"
                                            className="inline-flex items-center gap-x-2 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 transition"
                                        >
                                            <FolderIcon aria-hidden="true" className="size-5" />
                                            Save Meeting
                                        </button>
                                    )}
                                    <button
                                        className="rounded-md p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                                        onClick={() => {
                                            setSelectedForm(null);
                                            setMeeting(false);
                                        }}
                                        aria-label="Close"
                                    >
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>
                            </div>

                            <div className="grid h-[calc(100vh-80px)] grid-cols-1 lg:grid-cols-2">
                                <div className="overflow-auto border-r border-gray-200 bg-gray-50 p-4">
                                    <StaffDetails staff={{ id: Number(selectedForm.StaffId), Name: selectedForm.StaffName }} />
                                </div>

                                <div className="overflow-auto p-6">
                                    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Form Details</h4>
                                        <dl className="divide-y divide-gray-100">
                                            <div className="py-3"><dt className="text-sm font-medium text-gray-900">Full name</dt><dd className="text-sm text-gray-700">{selectedForm.StaffName}</dd></div>
                                            <div className="py-3"><dt className="text-sm font-medium text-gray-900">Sick Leave Period</dt><dd className="text-sm text-gray-700">{formattedDate(selectedForm.StartDate)} - {formattedDate(selectedForm.ReturnDate)}</dd></div>
                                            <div className="py-3"><dt className="text-sm font-medium text-gray-900">Reason</dt><dd className="text-sm text-gray-700">{selectedForm.Reason}</dd></div>
                                            <div className="py-3"><dt className="text-sm font-medium text-gray-900">Fit For Work</dt><dd className="text-sm text-gray-700">{selectedForm.FitForWork === 1 ? 'Yes' : 'No'}</dd></div>
                                            <div className="py-3"><dt className="text-sm font-medium text-gray-900">Self Certification</dt><dd className="text-sm text-gray-700">{selectedForm.SelfCert === 1 ? 'Yes' : 'No'}</dd></div>
                                            <div className="py-3"><dt className="text-sm font-medium text-gray-900">Doctor Letter</dt><dd className="text-sm text-gray-700">{selectedForm.DRLetter === 1 ? 'Yes' : 'No'}</dd></div>
                                            <div className="py-3"><dt className="text-sm font-medium text-gray-900">Adjustments</dt><dd className="text-sm text-gray-700">{selectedForm.Adjustments}</dd></div>
                                        </dl>
                                    </div>

                                    <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50/50 p-5">
                                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Sick Chat Entries</h4>
                                        {sickChatLoading && (
                                            <p className="text-sm text-gray-600">Loading sick chat entries...</p>
                                        )}
                                        {!sickChatLoading && sickChatError && (
                                            <p className="text-sm text-red-600">{sickChatError}</p>
                                        )}
                                        {!sickChatLoading && !sickChatError && sickChatEntries.length === 0 && (
                                            <p className="text-sm text-gray-600">No Sick Chat entries found for this form.</p>
                                        )}
                                        {!sickChatLoading && !sickChatError && sickChatEntries.length > 0 && (
                                            <div className="space-y-3">
                                                {sickChatEntries.map((entry) => (
                                                    <div key={entry.id || `${entry.SickDate}-${entry.Timestamp || ''}`} className="rounded-lg border border-emerald-100 bg-white p-3">
                                                        <p className="text-sm text-gray-900"><span className="font-semibold">SickDate:</span> {formattedDate(entry.SickDate)}</p>
                                                        <p className="text-sm text-gray-900 mt-1"><span className="font-semibold">Question:</span> {entry.Question || 'N/A'}</p>
                                                        <p className="text-sm text-gray-900 mt-1"><span className="font-semibold">Answer:</span> {entry.Answer || 'N/A'}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-6 rounded-xl border border-indigo-200 bg-indigo-50 p-5">
                                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Meeting Notes</h4>
                                        <label htmlFor="comment" className="block text-sm font-medium text-gray-900">Manager Comments</label>
                                        <textarea
                                            id="comment"
                                            name="comment"
                                            value={comment}
                                            onChange={(e) => setComment(e.target.value)}
                                            rows={5}
                                            className="mt-2 block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                        />

                                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label htmlFor="fit" className="block text-sm font-medium text-gray-900">Fit To Return Confirmed</label>
                                                <div className="mt-2 grid grid-cols-1">
                                                    <select
                                                        id="fit"
                                                        name="fit"
                                                        onChange={(e) => setFit(e.target.value)}
                                                        className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                                    >
                                                        <option value={1}>Yes</option>
                                                        <option value={0}>No</option>
                                                    </select>
                                                    <ChevronDownIcon aria-hidden="true" className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500" />
                                                </div>
                                            </div>
                                            <div>
                                                <label htmlFor="support" className="block text-sm font-medium text-gray-900">Further Support Required</label>
                                                <div className="mt-2 grid grid-cols-1">
                                                    <select
                                                        id="support"
                                                        name="support"
                                                        onChange={(e) => setSupport(e.target.value)}
                                                        className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                                    >
                                                        <option value={1}>Yes</option>
                                                        <option value={0}>No</option>
                                                    </select>
                                                    <ChevronDownIcon aria-hidden="true" className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Table Section */}
                <div className="w-full max-w-7xl mx-auto px-4 mb-10">
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                            <div>
                                <h1 className="text-base font-semibold text-gray-900">Sick Leave Records</h1>
                                <p className="mt-2 text-sm text-gray-700">
                                    A table of all sick leave forms and their details.
                                </p>
                            </div>
                            <button
                                className="bg-emerald-500 text-white px-4 py-2 rounded-md hover:bg-emerald-600 mt-4 md:mt-0"
                                onClick={handleSickLeaveReport}
                                data-component="backtowork-dashboard-actions"
                                data-feature="generate_sick_report_post"
                            >
                                Create Monthly Sick Leave Report
                            </button>
                        </div>

                        {filterStaffName !== 'all' && (
                            <div className="mb-6 rounded-lg border border-indigo-100 bg-indigo-50/60 p-5">
                                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold text-indigo-700">
                                            Sick trends for {filterStaffName}
                                        </p>
                                        {sickTrendsContent ? (
                                            <p className="mt-2 whitespace-pre-line text-sm text-gray-800">
                                                {sickTrendsContent}
                                            </p>
                                        ) : (
                                            <p className="mt-2 text-sm text-gray-500">
                                                No sick trends have been generated for this team member yet.
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-sm text-gray-600 md:text-right">
                                        <p className="font-medium uppercase tracking-wide text-gray-500">
                                            Last generated
                                        </p>
                                        <p className="mt-1 text-gray-900">
                                            {sickTrendsTimestamp ? formatDateTime(sickTrendsTimestamp) : 'Not available'}
                                        </p>
                                    </div>
                                </div>
                                {trendAnalysisLoading && (
                                    <div className="mt-3 flex items-center gap-2 text-sm text-indigo-700">
                                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            />
                                        </svg>
                                        Generating fresh insights...
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Filter Section */}
                        <div className="bg-gray-50 p-4 rounded-lg mb-4 border border-gray-200">
                            <h3 className="text-sm font-semibold text-gray-700 mb-3">Filter Records</h3>
                            
                            {/* Quick Filters */}
                            <div className="flex flex-wrap gap-2 mb-4">
                                <button
                                    onClick={() => setQuickFilter('lastMonth')}
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                                        quickFilter === 'lastMonth'
                                            ? 'bg-indigo-500 text-white'
                                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    Last Month
                                </button>
                                <button
                                    onClick={() => setQuickFilter('last6Months')}
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                                        quickFilter === 'last6Months'
                                            ? 'bg-indigo-500 text-white'
                                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    Last 6 Months
                                </button>
                                <button
                                    onClick={() => setQuickFilter('lastYear')}
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                                        quickFilter === 'lastYear'
                                            ? 'bg-indigo-500 text-white'
                                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    Last Year
                                </button>
                            </div>

                            {/* Custom Filters */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Start Date From
                                    </label>
                                    <input
                                        type="date"
                                        value={filterStartDate}
                                        onChange={(e) => setFilterStartDate(e.target.value)}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Return Date To
                                    </label>
                                    <input
                                        type="date"
                                        value={filterEndDate}
                                        onChange={(e) => setFilterEndDate(e.target.value)}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Staff Name
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={filterStaffName}
                                            onChange={(e) => setFilterStaffName(e.target.value)}
                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none"
                                        >
                                            <option value="all">All Staff</option>
                                            {uniqueStaffNames.map((name) => (
                                                <option key={name} value={name}>
                                                    {name}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDownIcon
                                            aria-hidden="true"
                                            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-wrap gap-2 mt-4">
                                {isFiltered && (
                                    <>
                                        <button
                                            onClick={clearFilters}
                                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm font-medium"
                                        >
                                            Clear Filters
                                        </button>
                                        <button
                                            onClick={handleTrendAnalysis}
                                            disabled={trendAnalysisLoading}
                                            className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                        >
                                            {trendAnalysisLoading ? (
                                                <>
                                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                    </svg>
                                                    Analyzing...
                                                </>
                                            ) : (
                                                '🔍 Trend Analysis'
                                            )}
                                        </button>
                                        <span className="px-4 py-2 bg-blue-50 text-blue-700 rounded-md text-sm font-medium">
                                            {filteredForms.length} record{filteredForms.length !== 1 ? 's' : ''} found
                                        </span>
                                    </>
                                )}
                            </div>
                            {isFiltered && (
                                <div className="mt-3 min-h-[1.5rem]">
                                    {trendAnalysisLoading && (
                                        <div className="flex items-center gap-2 text-sm text-indigo-600">
                                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            Generating new trend insights...
                                        </div>
                                    )}
                                    {!trendAnalysisLoading && trendStatusMessage && (
                                        <p className="text-sm text-emerald-700">{trendStatusMessage}</p>
                                    )}
                                </div>
                            )}
                        </div>

                        <div
                            className="overflow-x-auto rounded-lg border border-gray-100"
                            onMouseEnter={() => setTableHovered(true)}
                            onMouseLeave={() => setTableHovered(false)}
                        >
                            <div className="relative">
                                {!tableHovered && (
                                    <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none backdrop-blur-sm bg-white/60">
                                        <span className="text-lg font-semibold text-gray-700 bg-white/70 px-6 py-3 rounded shadow">
                                            Hover to view table
                                        </span>
                                    </div>
                                )}
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead>
                                        <tr>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">Id</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">CompanyCode</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">StaffId</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">Staff Name</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">Start Date</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">Return Date</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">Reason</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">Fit For Work</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">Adjustments</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">Self Cert</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">Doctor Letter</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">Manager Name</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">Meeting Held</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">Manager Notes</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">Fit ToReturn Confirmed</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">FurtherSupportRequired</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">ChecklistCompletedAt</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">CreatedAt</th>
                                            <th className="px-3 py-5 text-left text-sm font-semibold text-gray-900">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {(isFiltered ? filteredForms : allForms).map((form) => (
                                            <tr key={form.Id} className="h-20">
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">{form.Id}</td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">{form.CompanyCode}</td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">{form.StaffId}</td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">{form.StaffName}</td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">{formattedDate(form.StartDate)}</td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">{formattedDate(form.ReturnDate)}</td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">{form.Reason}</td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">{form.FitForWork === 1 ? "Yes" : "No"}</td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">{form.Adjustments}</td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">{form.SelfCert === 1 ? "Yes" : "No"}</td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">{form.DRLetter === 1 ? "Yes" : "No"}</td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">{form.ManagerName}</td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">{form.MeetingHeld === 1 ? "Yes" : "No"}</td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">{form.ManagerNotes}</td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">{form.FitToReturnConfirmed === 1 ? "Yes" : "No"}</td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">{form.FurtherSupportRequired === 1 ? "Yes" : "No"}</td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">
                                                    {form.ChecklistCompletedAt ? format(parseISO(form.ChecklistCompletedAt), 'dd/MM/yyyy HH:mm') : ""}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">
                                                    {form.CreatedAt ? format(parseISO(form.CreatedAt), 'dd/MM/yyyy HH:mm') : ""}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-6 text-sm text-gray-900">
                                                    <button
                                                        onClick={() => handleDownloadFormPdf(getBackToWorkFormId(form))}
                                                        className="inline-flex items-center gap-1 rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white hover:bg-indigo-700"
                                                    >
                                                        <DocumentArrowDownIcon className="h-4 w-4" />
                                                        Send PDF
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Alerts */}
                <div className="fixed top-4 right-4 z-50">
                    <MeetingAlert />
                </div>
                <div className="fixed top-4 right-4 z-50">
                    <MessageAlert />
                </div>
                <div className="fixed top-4 right-4 z-50">
                    <ShiftsAlert />
                </div>
                {pdfSendVisible && (
                    <div className="fixed top-20 right-4 z-50">
                        <div className={`rounded-md px-4 py-2 shadow-md text-sm ${pdfSendType === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
                            {pdfSendMessage}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
